import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getMembershipTier } from '../config/membership';
import { getMembership, activateMembershipTrip } from '../utils/api';
import { trackEvent } from '../utils/tracking';
import './MembershipConfirmation.css';

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL;
const RETRY_DELAYS_MS = [1500, 2000];

export default function MembershipConfirmation() {
  const [searchParams] = useSearchParams();
  const membershipId = searchParams.get('membership');
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [destination, setDestination] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState(null);

  useEffect(() => {
    trackEvent('Page Viewed', { page: 'membership-confirmation' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!membershipId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const fetchMembership = async (attempt) => {
      try {
        const result = await getMembership(membershipId);
        if (cancelled) return;
        setMembership(result);
        setLoading(false);
      } catch {
        if (cancelled) return;
        if (attempt < RETRY_DELAYS_MS.length) {
          setTimeout(() => fetchMembership(attempt + 1), RETRY_DELAYS_MS[attempt]);
        } else {
          setLoading(false);
        }
      }
    };

    fetchMembership(0);
    return () => { cancelled = true; };
  }, [membershipId]);

  useEffect(() => {
    if (!membership || !membershipId) return;
    const dedupeKey = `tp_membership_tracked_${membershipId}`;
    if (sessionStorage.getItem(dedupeKey)) return;

    trackEvent('Membership Purchase Completed', {
      membershipId,
      quantity: membership.quantity,
      revenue: membership.amountCents / 100,
    });
    sessionStorage.setItem(dedupeKey, '1');
  }, [membership, membershipId]);

  const tripsRemaining = membership ? membership.tripsAllotted - membership.tripsActivated : 0;
  const canActivate = membership?.status === 'active' && tripsRemaining > 0;

  const handleActivate = async (e) => {
    e.preventDefault();
    setActivating(true);
    setActivateError(null);
    try {
      const result = await activateMembershipTrip({ membershipId, destination, departureDate });
      setMembership((prev) => ({
        ...prev,
        tripsActivated: result.membership.trips_activated,
        activations: result.activations.map((a) => ({
          activationId: a.activation_id,
          destination: a.destination,
          departureDate: a.departure_date,
          status: a.status,
          loungeStatus: a.lounge_status,
        })),
      }));
      trackEvent('Trip Activated', { membershipId, destination });
      setDestination('');
      setDepartureDate('');
    } catch (err) {
      setActivateError(err.body?.error || 'Something hiccuped activating that trip. Try again?');
      trackEvent('Error Occurred', { step: 'membership-activate', message: err.message });
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="tp-membership-confirmation">
      <Header />
      <div className="container tp-membership-confirmation__content">
        <h1 className="headline-1">You're a Travel+ Annual member</h1>
        <p className="body-l tp-membership-confirmation__subhead">
          Activate a trip whenever one's coming up — your eSIM and lounge pass go out right away.
        </p>

        {!loading && membership && (
          <>
            <div className="card tp-membership-confirmation__status">
              <p className="title-m">
                {getMembershipTier(membership.tier)?.name || 'Travel+ Annual'} — {tripsRemaining} of {membership.tripsAllotted} trips left this year
              </p>
              <p className="body-s tp-membership-confirmation__renewal">
                Renews {new Date(membership.renewalDate).toLocaleDateString()}
              </p>
            </div>

            <form className="card tp-membership-confirmation__form" onSubmit={handleActivate}>
              <h2 className="title-l">Activate a trip</h2>
              {canActivate ? (
                <>
                  <div className="tp-membership-confirmation__field">
                    <label className="label-m" htmlFor="tp-destination">Destination</label>
                    <input
                      id="tp-destination"
                      type="text"
                      className="input tp-membership-confirmation__input"
                      value={destination}
                      onChange={(e) => setDestination(e.target.value)}
                      placeholder="Thailand"
                      required
                    />
                  </div>
                  <div className="tp-membership-confirmation__field">
                    <label className="label-m" htmlFor="tp-departure">Departure date</label>
                    <input
                      id="tp-departure"
                      type="date"
                      className="input tp-membership-confirmation__input"
                      value={departureDate}
                      onChange={(e) => setDepartureDate(e.target.value)}
                      required
                    />
                  </div>
                  {activateError && <p className="body-s tp-membership-confirmation__error">{activateError}</p>}
                  <button type="submit" className="btn btn-primary" disabled={activating}>
                    {activating ? 'Activating…' : 'Activate this trip'}
                  </button>
                </>
              ) : (
                <p className="body-m">
                  {membership.status !== 'active'
                    ? 'This membership is no longer active.'
                    : "You've used all your trips for this year — see you next renewal."}
                </p>
              )}
            </form>

            {membership.activations?.length > 0 && (
              <div className="card tp-membership-confirmation__history">
                <h2 className="title-l">Activated trips</h2>
                <ul className="tp-membership-confirmation__list">
                  {membership.activations.map((a) => (
                    <li key={a.activationId} className="body-m">
                      {a.destination || 'Trip'} — {a.departureDate || 'no date'} · {a.status === 'issued' ? 'eSIM + lounge pass sent' : 'processing'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        <p className="body-s tp-membership-confirmation__footnote">
          Everything above is also sent to {membership?.email || 'your email'}. Questions? {SUPPORT_EMAIL} — a real human answers.
        </p>
      </div>
      <Footer />
    </div>
  );
}
